// Offline licence banner for customer-hosted installs. Rendered once by PrivateRoute above every
// authenticated page; reads the `licence` status from FeatureFlagsContext (GET /config/features).
// Hidden on the SaaS (notRequired) and while the licence is valid.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "react-feather";
import { useLicenceStatus } from "../contexts/FeatureFlagsContext";
import { useRBAC } from "../hooks/useRBAC";

const GRACE_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
const DISMISS_KEY_PREFIX = "axaris-licence-banner-dismissed:";

// Licence dates are UTC instants issued on day boundaries; show them as the UTC calendar date so
// every viewer sees the date printed on the licence.
export const formatLicenceDate = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
};

export const graceEndDate = (expiresAt) => {
  const time = expiresAt ? new Date(expiresAt).getTime() : NaN;
  return Number.isNaN(time) ? null : new Date(time + GRACE_DAYS * DAY_MS);
};

const bannerMessage = (licence) => {
  switch (licence.state) {
    case "expiringSoon":
      return `This installation's Axaris licence expires on ${formatLicenceDate(licence.expiresAt)}. Ask your administrator to renew it.`;
    case "grace":
      return `This installation's Axaris licence expired on ${formatLicenceDate(licence.expiresAt)}. Axaris keeps working until ${formatLicenceDate(graceEndDate(licence.expiresAt))}; after that you can view your data but not start new assessments.`;
    case "readOnly":
      return "This installation's Axaris licence has expired. You can view and export your data, but new assessments and organizations are disabled until a new licence is installed.";
    case "none":
      return "No Axaris licence is installed on this installation. Ask your administrator to install one.";
    default:
      return null;
  }
};

const isDismissible = (state) => state === "expiringSoon" || state === "grace";

// Dismissal lasts for the browser session and only for the state/expiry it was dismissed in, so a
// later state change (expiring soon -> grace) shows the banner again.
const dismissKey = (licence) => `${DISMISS_KEY_PREFIX}${licence.state}:${licence.expiresAt || ""}`;

const readDismissed = (key) => {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const LicenceBanner = () => {
  const licence = useLicenceStatus();
  const { isSuperAdmin } = useRBAC();
  const [dismissedKey, setDismissedKey] = useState(null);

  const message = bannerMessage(licence || {});
  if (!message) return null;

  const dismissible = isDismissible(licence.state);
  const key = dismissKey(licence);
  if (dismissible && (dismissedKey === key || readDismissed(key))) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(key, "1");
    } catch {
      // Storage unavailable: dismiss for this page view only.
    }
    setDismissedKey(key);
  };

  const styles = dismissible
    ? "border-status-warning bg-status-warning-bg text-status-warning"
    : "border-status-error bg-status-error-bg text-status-error";

  return (
    <div
      role={dismissible ? "status" : "alert"}
      data-testid="licence-banner"
      className={`flex items-start gap-3 border-b px-4 py-2.5 text-sm ${styles}`}
    >
      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <p className="flex-1">
        {message}
        {isSuperAdmin() && (
          <>
            {" "}
            <Link to="/super-admin" className="font-medium underline">
              Manage licence
            </Link>
          </>
        )}
      </p>
      {dismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          className="p-0.5 opacity-80 hover:opacity-100"
          aria-label="Dismiss licence notice"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default LicenceBanner;
